import { AddressElement } from '@stripe/react-stripe-js';
import {
  StripeAddressElement,
  StripeAddressElementChangeEvent,
} from '@stripe/stripe-js';
import { ShipEngineAddress } from '@util/firestore/shipengine';
import { stripeAddressToAddressDocument } from '@util/firestore/stripe';
import { validateAddresses } from '@util/firestore/users';
import { debounce } from '@util/index';
import {
  AddressDocument,
  AddressDocumentDraft,
} from '@util/types/firestore/address';
import { useAuth } from 'context/AuthContext';
import React from 'react';

export type StripeAddressChangeEvent =
  | {
      status: 'valid';
      result: AddressDocumentDraft;
      isNewAddress: boolean;
    }
  | {
      status: 'invalid';
      matched_address: ShipEngineAddress;
    }
  | {
      status: 'incomplete';
      result: StripeAddressElementChangeEvent;
    }
  | {
      status: 'validating';
    }
  | {
      /**
       * GB web#1743. ShipEngine couldn't verify the address exactly as
       * typed, but a normalized variant of it does verify (a CA fractional
       * house number or hyphenated postal code). `typed` is what the user
       * actually entered; `alternative` is ShipEngine's confirmed match for
       * the normalized form. Bill's ruling 2026-08-21: never auto-accept
       * either one - the consumer MUST show both and let the user pick.
       */
      status: 'needs_confirmation';
      typed: AddressDocumentDraft;
      alternative: AddressDocumentDraft;
    };

type StripeAddressFormProps = {
  onChange?: (e: StripeAddressChangeEvent) => void;
  onReady?: (e: StripeAddressElement) => void;
  mode: 'shipping' | 'billing';
  addresses?: AddressDocument[];
  displayNameOption?: 'split' | 'full' | 'organization';
  defaultValues?: AddressDocument;
  /**
   * If `true`, the AddressElement will NOT collect phone (`fields.phone: 'never'`)
   * and the caller is expected to collect phone via a separate input and pass
   * it in via the `phone` prop. Used by V3 checkout (InformationSection) so
   * address verification can fire as soon as the address sub-fields are
   * complete, independent of phone entry.
   *
   * Default `false` preserves the original behavior: AddressElement collects
   * phone (`fields.phone: 'always'`), validation requires it, and `contacts`
   * /`defaults` populate phone for Stripe's saved-address dropdown. This is
   * what every non-V3 caller expects (CreateListingPage, ShopSettings, seller
   * registration forms, AddAddressModal).
   */
  collectPhoneExternally?: boolean;
  /**
   * Phone collected externally (when `collectPhoneExternally: true`). Read
   * via a ref inside `handleChange` to avoid stale-closure bugs under the
   * 500ms debounce.
   */
  phone?: string;
};

const StripeAddressForm = ({
  onChange,
  onReady,
  mode,
  addresses,
  displayNameOption = 'full',
  defaultValues,
  collectPhoneExternally = false,
  phone = '',
}: StripeAddressFormProps) => {
  const [error, setError] = React.useState('');

  // Sync phone into a ref every render so the debounced handler always reads
  // the latest value rather than the closure captured at scheduling time.
  // Only used when collectPhoneExternally is true.
  const phoneRef = React.useRef(phone);
  phoneRef.current = phone;

  // contacts populates Stripe's saved-address dropdown. When phone is
  // collected externally (fields.phone: 'never'), Stripe rejects passing
  // phone in contacts — strip it. Otherwise include it as before.
  const contacts = addresses?.map((a) =>
    collectPhoneExternally
      ? {
          name: a.name,
          address: {
            line1: a.address_line1,
            line2: a.address_line2,
            city: a.city_locality,
            state: a.state_province ?? '',
            postal_code: a.postal_code,
            country: a.country_code,
          },
        }
      : {
          name: a.name,
          phone: a.phone,
          address: {
            line1: a.address_line1,
            line2: a.address_line2,
            city: a.city_locality,
            state: a.state_province ?? '',
            postal_code: a.postal_code,
            country: a.country_code,
          },
        }
  );
  // defaults pre-populates the AddressElement. Same constraint as contacts:
  // strip phone when fields.phone: 'never'.
  const defaults =
    defaultValues &&
    (collectPhoneExternally
      ? {
          name: defaultValues.name,
          address: {
            line1: defaultValues.address_line1,
            line2: defaultValues.address_line2,
            city: defaultValues.city_locality,
            state: defaultValues.state_province ?? '',
            postal_code: defaultValues.postal_code,
            country: defaultValues.country_code,
          },
        }
      : {
          name: defaultValues.name,
          phone: defaultValues.phone,
          address: {
            line1: defaultValues.address_line1,
            line2: defaultValues.address_line2,
            city: defaultValues.city_locality,
            state: defaultValues.state_province ?? '',
            postal_code: defaultValues.postal_code,
            country: defaultValues.country_code,
          },
        });
  const handleChange = async (e: StripeAddressElementChangeEvent) => {
    if (e.complete) {
      onChange?.({
        status: 'validating',
      });
      const [firstName, ...lastName] = e.value.name.split(' ');
      // min width of 2 characters for each name
      if (firstName.length < 2 || lastName.join(' ').length < 2) {
        setError('First and last name must have at least two characters.');
        onChange?.({
          status: 'incomplete',
          result: e,
        });
        return;
      }
      // Phone source: when collected externally, use our ref; otherwise let
      // stripeAddressToAddressDocument fall back to e.value.phone (which is
      // present because fields.phone: 'always' is set by default).
      const overridePhone = collectPhoneExternally ? phoneRef.current : undefined;
      const address = stripeAddressToAddressDocument(e, overridePhone);
      const res = await validateAddresses([address]);

      if (res[0].status === 'verified' || res[0].status === 'warning') {
        // ShipEngine returns its own ShipEngineAddress shape (extra fields,
        // missing AddressDocument's `is_default`). Pick the AddressDocument
        // fields explicitly. Phone source matches the override decision above.
        const matched = res[0].matched_address;
        const finalPhone = collectPhoneExternally
          ? phoneRef.current
          : (e.value.phone ?? '');
        const draft: AddressDocumentDraft = {
          name: matched.name,
          phone: finalPhone,
          city_locality: matched.city_locality,
          country_code: matched.country_code,
          postal_code: matched.postal_code,
          address_line1: matched.address_line1,
          address_line2: matched.address_line2,
          state_province: matched.state_province ?? '',
          is_default: false,
        };
        onChange?.({
          status: 'valid',
          result: draft,
          isNewAddress: e.isNewAddress,
        });
        setError('');
      } else if (res[0].ca_confirmable_alternative?.matched_address) {
        // GB web#1743: ShipEngine rejected the address as typed (fractional
        // house number / hyphenated postal), but a normalized variant of it
        // verifies. Never auto-accept either form - hand both to the caller
        // so the user can explicitly choose. `address` is already the exact
        // AddressDocumentDraft shape for what the user typed. Guarded on
        // `matched_address` being present (not just the wrapper object) -
        // if it's ever missing, fall through to the standard error/unverified
        // branch below rather than dereferencing an address we don't have.
        const alt = res[0].ca_confirmable_alternative.matched_address;
        const finalPhone = collectPhoneExternally
          ? phoneRef.current
          : (e.value.phone ?? '');
        const alternative: AddressDocumentDraft = {
          name: alt.name || address.name,
          phone: finalPhone,
          city_locality: alt.city_locality,
          country_code: alt.country_code,
          postal_code: alt.postal_code,
          address_line1: alt.address_line1,
          address_line2: alt.address_line2,
          state_province: alt.state_province ?? '',
          is_default: false,
        };
        onChange?.({
          status: 'needs_confirmation',
          typed: address,
          alternative,
        });
        // This banner renders in EVERY StripeAddressForm consumer, but only
        // AddAddressModal currently renders a richer confirm-choice UI for
        // `needs_confirmation`. Clearing this to '' would leave the other 8
        // consumers (checkout, seller registration/KYC, listing ship-from,
        // shop settings) with a disabled Continue/Save button and ZERO
        // visible explanation - turning a loud, generic ShipEngine failure
        // into a silent dead end. Keep a generic-but-honest message here so
        // every consumer shows SOMETHING; AddAddressModal's own panel adds
        // the specific choice on top of it.
        setError(
          "We couldn't fully verify this address, but it may still be deliverable. Please double-check it."
        );
      } else if (res[0].status === 'error' || res[0].status === 'unverified') {
        setError(
          res[0]?.messages[0]?.message ??
            'Unable to verify address. Please contact support.'
        );
        onChange?.({
          status: 'invalid',
          matched_address: res[0].matched_address,
        });
      }
    } else {
      onChange?.({
        status: 'incomplete',
        result: e,
      });
    }
  };
  const debouncedHandleChange = debounce<(e: StripeAddressElementChangeEvent) => Promise<void>>(handleChange, 500);

  return (
    <>
      {error && (
        <div className=" flex justify-center rounded-[10px] bg-red-200 p-[1.6rem]">
          <span className="font-semibold text-red-500">{error}</span>
        </div>
      )}
      <AddressElement
        options={{
          mode: mode,
          display: { name: displayNameOption },
          // Stripe.js injects the Google Maps Places script into this document itself using this key when
          // no matching <script> exists (verified in js.stripe.com/v3 loader, 2026-09-04). Do NOT add a
          // global Maps <script> in _document.tsx to silence Google's 'loading=async' console warning — a
          // preloaded tag can leave Stripe's cached loader promise unresolved and kill autocomplete for the
          // page session (PR #1801, GB #1802). On /checkout, Stripe uses its OWN Maps key because the
          // Address Element is grouped with the Payment Element; this key is exercised only on the
          // standalone address surfaces (seller registration, add-address modal, create listing, shop
          // settings).
          autocomplete: {
            mode: 'google_maps_api',
            apiKey: 'AIzaSyBu5OT972LTDMxAvfCRMnvvURl0yF6KHBM',
          },
          fields: {
            phone: collectPhoneExternally ? 'never' : 'always',
          },
          contacts,
          defaultValues: defaults,
          // When the AddressElement collects phone, it must be required so
          // `e.complete` reflects "address + phone both filled" — preserves
          // the pre-#1247 behavior for all non-V3 callers. When phone is
          // collected externally, validation is moot (the field doesn't
          // render).
          ...(collectPhoneExternally
            ? {}
            : { validation: { phone: { required: 'always' as const } } }),
        }}
        onChange={debouncedHandleChange}
        onReady={onReady}
      />
    </>
  );
};

export default StripeAddressForm;
